
import React from "react";
import ReactDOM from "react-dom/client";

import "./index.css";
import "./css/core/Theme.module.css";

import App from "./App";
import reportWebVitals from "./reportWebVitals";

/* ==========================================================
   TOWER MAINTENANCE DIAGNOSTIC LOGGER
   Temporary pre-production debugging
========================================================== */

const LOG_PREFIX = "[TOWER-DIAG]";
const STORAGE_KEY = "tower_maintenance_diagnostic_v1";
const MAX_LOGS = 150;

// Enable during testing; disable before production release.
const DEBUG_ENABLED =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_TOWER_DIAGNOSTICS === "true";

const getStoredLogs = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveLog = (entry) => {
  try {
    const logs = getStoredLogs();

    logs.push(entry);

    sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(logs.slice(-MAX_LOGS)),
    );
  } catch {
    // Diagnostics must never break the CRM application.
  }
};

const sanitizeDetails = (details) => {
  if (
    !details ||
    typeof details !== "object" ||
    Array.isArray(details)
  ) {
    return {};
  }

  // Explicitly allow only nonsensitive diagnostic properties.
  const allowedKeys = [
    "fieldName",
    "stage",
    "status",
    "code",
    "reason",
    "source",
    "method",
    "httpStatus",
    "durationMs",
    "fileSizeBytes",
    "fileType",
    "width",
    "height",
    "progress",
    "hasTowerId",
    "hasFile",
    "hasCoordinates",
    "hasTimestamp",
    "hasSnapshotPath",
    "visibilityState",
    "persisted",
    "online",
    "wasDiscarded",
    "errorName",
    "captureId",
  ];

  const result = {};

  for (const key of allowedKeys) {
    const value = details[key];

    if (
      value !== undefined &&
      value !== null &&
      (
        typeof value === "string" ||
        typeof value === "number" ||
        typeof value === "boolean"
      )
    ) {
      result[key] =
        typeof value === "string"
          ? value.slice(0, 120)
          : value;
    }
  }

  return result;
};

const towerDiagnostic = (event, details = {}) => {
  if (!DEBUG_ENABLED) return;

  const entry = {
    time: new Date().toISOString(),
    event: String(event).slice(0, 100),
    details: sanitizeDetails(details),
  };

  saveLog(entry);

  console.log(
    `${LOG_PREFIX} ${entry.time} ${entry.event}`,
    entry.details,
  );
};

if (DEBUG_ENABLED && typeof window !== "undefined") {
  window.towerDiagnostic = towerDiagnostic;

  // Show the previous camera session's history after a reload.
  const previousLogs = getStoredLogs();

  console.group(`${LOG_PREFIX} Previous / current session`);

  previousLogs.slice(-30).forEach((entry) => {
    console.log(entry.time, entry.event, entry.details);
  });

  console.groupEnd();

  towerDiagnostic("APP_SCRIPT_STARTED", {
    status: "initializing",
    online: navigator.onLine,
    visibilityState: document.visibilityState,
    wasDiscarded: document.wasDiscarded === true,
  });

  window.addEventListener("pageshow", (event) => {
    towerDiagnostic("PAGE_SHOW", {
      persisted: event.persisted,
      visibilityState: document.visibilityState,
      wasDiscarded: document.wasDiscarded === true,
    });
  });

  window.addEventListener("pagehide", (event) => {
    towerDiagnostic("PAGE_HIDE", {
      persisted: event.persisted,
      visibilityState: document.visibilityState,
    });
  });

  window.addEventListener("beforeunload", () => {
    towerDiagnostic("BEFORE_UNLOAD", {
      status: "page_leaving",
    });
  });

  document.addEventListener("visibilitychange", () => {
    towerDiagnostic("VISIBILITY_CHANGED", {
      visibilityState: document.visibilityState,
    });
  });

  window.addEventListener("focus", () => {
    towerDiagnostic("WINDOW_FOCUS");
  });

  window.addEventListener("blur", () => {
    towerDiagnostic("WINDOW_BLUR");
  });

  window.addEventListener("online", () => {
    towerDiagnostic("NETWORK_ONLINE");
  });

  window.addEventListener("offline", () => {
    towerDiagnostic("NETWORK_OFFLINE");
  });

  window.addEventListener("error", (event) => {
    towerDiagnostic("GLOBAL_JS_ERROR", {
      status: "error",
      errorName: event.error?.name || "Error",
      code: "UNCAUGHT_ERROR",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    towerDiagnostic("UNHANDLED_PROMISE_REJECTION", {
      status: "error",
      errorName: event.reason?.name || "PromiseRejection",
      code: "UNHANDLED_REJECTION",
    });
  });

  // These helpers are for pre-production debugging only.
  window.showTowerLogs = () => {
    const logs = getStoredLogs();

    console.table(
      logs.map((item) => ({
        time: item.time,
        event: item.event,
        ...item.details,
      })),
    );

    return logs;
  };

  window.clearTowerLogs = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Ignore storage restrictions.
    }

    console.log(`${LOG_PREFIX} History cleared`);
  };

  towerDiagnostic("GLOBAL_LISTENERS_READY", {
    status: "ready",
  });
}

/* ==========================================================
   REACT APPLICATION
========================================================== */

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("React root element was not found.");
}

towerDiagnostic("REACT_ROOT_FOUND", {
  status: "ready",
});

const root = ReactDOM.createRoot(rootElement);

towerDiagnostic("REACT_RENDER_START");

root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

towerDiagnostic("REACT_RENDER_REQUESTED");

/* ==========================================================
   WEB VITALS
========================================================== */

reportWebVitals();
